/**
 * FAQ Page - Olympic Fit Wellness
 * Frequently Asked Questions
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowRight, MessageCircle } from 'lucide-react';

export default function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqCategories = [
    {
      title: 'Membership',
      questions: [
        {
          q: 'What membership options do you offer?',
          a: 'We offer three membership tiers: Essentials ($49/month), Wellness ($99/month), and Elite ($199/month). Each tier provides increasing levels of access to our facilities, classes, and services. All memberships include access to all 15+ locations.'
        },
        {
          q: 'Is there a free trial available?',
          a: 'Yes! We offer a 7-day free trial for new members. During your trial, you\'ll have full access to our facilities, group classes, and recovery services. No credit card required to start.'
        },
        {
          q: 'Can I freeze or cancel my membership?',
          a: 'Yes, you can freeze your membership for up to 3 months per year at no additional cost. Cancellations require 30 days notice and can be done online or at any location.'
        },
        {
          q: 'Do you offer family or corporate memberships?',
          a: 'Yes, we offer family add-on rates and corporate wellness programs. Contact us for custom pricing based on your needs.'
        }
      ]
    },
    {
      title: 'Classes & Training',
      questions: [
        {
          q: 'How do I book classes?',
          a: 'You can book classes through our mobile app, website member portal, or at any location\'s front desk. Classes can be booked up to 7 days in advance.'
        },
        {
          q: 'What if I need to cancel a class booking?',
          a: 'Cancellations made at least 4 hours before class time will not incur any penalty. Late cancellations or no-shows may result in a booking restriction.'
        },
        {
          q: 'Are classes suitable for beginners?',
          a: 'Absolutely! Most of our classes offer modifications for all fitness levels. Look for classes marked "All Levels" or "Beginner-Friendly" in our schedule.'
        },
        {
          q: 'How does personal training work?',
          a: 'Personal training sessions can be purchased as packages or à la carte. Your trainer will conduct an initial assessment, create a customized program, and track your progress over time.'
        }
      ]
    },
    {
      title: 'Facilities & Services',
      questions: [
        {
          q: 'What amenities are included with membership?',
          a: 'All memberships include access to our fitness floors, locker rooms with showers, towel service, and group fitness classes. Higher tier memberships include recovery services, guest passes, and more.'
        },
        {
          q: 'Do you have childcare services?',
          a: 'Yes, our Kids Wellness Club is available at select locations. It\'s complimentary with Wellness and Elite memberships, or available for a small fee for Essentials members.'
        },
        {
          q: 'What are your hours of operation?',
          a: 'Most locations are open 5:00 AM - 11:00 PM Monday-Friday, 6:00 AM - 9:00 PM Saturday-Sunday. Some locations offer 24/7 access for Elite members.'
        },
        {
          q: 'Is parking available?',
          a: 'Yes, all of our locations offer free parking for members. Most locations also have bike racks and some offer EV charging stations.'
        }
      ]
    },
    {
      title: 'Nutrition & Recovery',
      questions: [
        {
          q: 'What nutrition services do you offer?',
          a: 'We offer one-on-one consultations with Registered Dietitians, custom meal planning, macro coaching, metabolic testing, and meal prep workshops.'
        },
        {
          q: 'What recovery services are available?',
          a: 'Our recovery suite includes infrared saunas, hydromassage beds, red light therapy, cryotherapy (at select locations), compression therapy, and sports massage.'
        },
        {
          q: 'Are nutrition consultations included in membership?',
          a: 'Wellness and Elite members receive complimentary nutrition consultations. Essentials members can add nutrition services for an additional fee.'
        }
      ]
    },
    {
      title: 'App & Virtual',
      questions: [
        {
          q: 'How do I download the OFW app?',
          a: 'The Olympic Fit Wellness app is available for free on iOS (App Store) and Android (Google Play). Simply search for "Olympic Fit Wellness" to download.'
        },
        {
          q: 'What can I do in the app?',
          a: 'The app allows you to book classes, log workouts, track nutrition, access on-demand videos, connect with trainers, participate in challenges, and manage your membership.'
        },
        {
          q: 'Can I access virtual classes without a gym membership?',
          a: 'We offer a digital-only membership option that provides access to all on-demand content and live virtual classes. Contact us for pricing.'
        }
      ]
    }
  ];

  const toggleQuestion = (categoryIdx, questionIdx) => {
    const key = `${categoryIdx}-${questionIdx}`;
    setOpenIndex(openIndex === key ? null : key);
  };

  return (
    <div className="bg-white" data-testid="faq-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">SUPPORT</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            Frequently Asked Questions
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            Find answers to common questions about membership, classes, services, and more.
          </p>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            {faqCategories.map((category, catIdx) => (
              <div key={catIdx} className="mb-12">
                <h2 className="text-xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.06em] uppercase border-b border-neutral-200 pb-4">
                  {category.title}
                </h2>
                <div className="space-y-2">
                  {category.questions.map((item, qIdx) => {
                    const key = `${catIdx}-${qIdx}`;
                    const isOpen = openIndex === key;
                    return (
                      <div key={qIdx} className="border border-neutral-200">
                        <button
                          onClick={() => toggleQuestion(catIdx, qIdx)}
                          className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-neutral-50 transition-colors"
                          data-testid={`faq-${catIdx}-${qIdx}`}
                        >
                          <span className="font-medium text-neutral-900 pr-8">{item.q}</span>
                          <ChevronDown className={`w-5 h-5 text-neutral-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-5">
                            <p className="text-neutral-600 tracking-wide leading-relaxed">{item.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <MessageCircle className="w-12 h-12 text-neutral-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-heading font-normal text-neutral-900 mb-4 tracking-[0.04em] uppercase">
              Still Have Questions?
            </h2>
            <p className="text-neutral-600 mb-8 tracking-wide">
              Our team is here to help. Reach out to us and we'll get back to you within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-black text-white font-heading px-8 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-colors"
              >
                CONTACT US <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+14695550100"
                className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 font-heading px-8 py-4 text-sm tracking-[0.08em] hover:border-black transition-colors"
              >
                CALL (469) 555-0100
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
